// A slide designed by hand (S5, 25 Oct 2026, David: "a slimmed-down version of PowerPoint where you can create your own
// format"). Every slide can hold boxes a person or an agent puts anywhere on it (text, shapes, lines, pictures and live
// widgets, moved, resized, rotated, layered and styled), its layout's own fields can be moved and restyled like boxes
// (`place`, PowerPoint's placeholders: they stay the layout's title and text), and it can wear a background of its own
// (`bg`, which S6's themes write). Shared by the board, the server and the agents' tools; light on purpose.
//
// Everything is in the slide's design space, 1600 by 900, whatever size it is drawn at. Colours are the deck's theme
// colours by name (they follow the theme, as PowerPoint's do) or a hex; fonts are the deck's title or body font, or one
// of a curated list the board hosts. Nothing else: no CSS, addresses or HTML in a slide's data.
//
// Two writers changing different boxes of one slide at once both keep theirs: a change goes to the server as the boxes
// it changed (`boxSet`, by id; null removes one) and the fields it changed (`placeSet`, by field), laid over what the
// slide has (wireSlide here, cleanSlide in shared/slides.ts).

import { cleanPictureRef, type PictureRef } from "./imageInfo";
import type { DocBody, DocMark, DocNode } from "./docblocks";
import type { Entry } from "./types";
import { cleanTable, tableLimits, type SlideTable } from "./slideTable";
import { DASHES, cleanBoxFormat, type BoxFormat, type DashStyle } from "./slideFormat";
import { MAX_LEVELS, cleanLineMore, cleanTextMore, num, type LineMore, type TextMore } from "./slidePlace";
import { chartLimits, cleanChart, type SlideChart } from "./slideChart";
import { cleanDiagram, type SlideDiagram } from "./slideDiagram";

const W = 1600;
const H = 900;

// ---------------------------------------------------------------------------
// colours

/**
 * A deck's theme colours, as PowerPoint's and Google Slides' themes name theirs: each deck style gives every one a value
 * (src/boards/slides/look.ts), and a box that uses one changes with the theme.
 */
export const THEME_COLORS = [
  { id: "background", name: "Background" },
  { id: "surface", name: "Surface" },
  { id: "text", name: "Text" },
  { id: "muted", name: "Muted text" },
  { id: "accent", name: "Accent" },
  { id: "accent2", name: "Accent 2" },
  { id: "accent3", name: "Accent 3" },
  { id: "accent4", name: "Accent 4" },
] as const;

export type ThemeColorId = (typeof THEME_COLORS)[number]["id"];
/** A theme colour by name, or a colour of its own as #RRGGBB. */
export type SlideColor = ThemeColorId | `#${string}`;
/** A theme's colours, each as #RRGGBB. */
export type Palette = Record<ThemeColorId, string>;

const THEME_IDS = new Set<string>(THEME_COLORS.map((c) => c.id));
const COLOR_ALIAS: Record<string, ThemeColorId> = {
  bg: "background",
  ground: "background",
  slide: "background",
  card: "surface",
  panel: "surface",
  fg: "text",
  ink: "text",
  body: "text",
  title: "text",
  "muted-text": "muted",
  secondary: "muted",
  subtle: "muted",
  accent1: "accent",
  primary: "accent",
};

export function isThemeColor(x: unknown): x is ThemeColorId {
  return typeof x === "string" && THEME_IDS.has(x);
}

/** A colour as kept: a theme colour's name (or a word for one), or #RRGGBB in capitals; null for anything else. */
export function cleanColor(raw: unknown): SlideColor | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  const k = s.toLowerCase().replace(/[\s_]+/g, "-");
  if (THEME_IDS.has(k)) return k as ThemeColorId;
  const plain = k.replace(/-/g, "");
  if (THEME_IDS.has(plain)) return plain as ThemeColorId;
  if (COLOR_ALIAS[k]) return COLOR_ALIAS[k];
  if (k === "white") return "#FFFFFF";
  if (k === "black") return "#000000";
  const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(s);
  if (!m) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join("") : m[1];
  return `#${h.toUpperCase()}`;
}

/** A colour as drawn: a theme colour's value in this palette, a hex as it is. */
export function colorValue(c: SlideColor | null | undefined, palette: Palette, fallback: string): string {
  if (!c) return fallback;
  return isThemeColor(c) ? palette[c] : c;
}

// ---------------------------------------------------------------------------
// fonts

export type FontKind = "sans" | "serif" | "display" | "mono" | "hand";

export interface FontSpec {
  id: string;
  /** its name in the font menu, and what a PowerPoint file names */
  name: string;
  kind: FontKind;
  /** the family the board registers it under (src/boards/slides/fonts.ts) */
  family: string;
  /** what stands in while it loads, and where it is missing */
  fallback: string;
  /** the weights it has: a range for a variable font, else the ones the board hosts */
  weights: [number, number];
}

const SANS_FB = "ui-sans-serif, system-ui, sans-serif";
const SERIF_FB = "Georgia, 'Times New Roman', serif";

/**
 * The fonts a deck can use beyond its theme's: chosen for slides (a few sans for body and headlines, serifs, display
 * faces, a mono and a hand), each self-hosted (Latin only) and loaded only when a slide shows it. All SIL Open Font
 * License 1.1.
 */
export const FONTS: FontSpec[] = [
  { id: "geist", name: "Geist", kind: "sans", family: "Geist Variable", fallback: SANS_FB, weights: [100, 900] },
  { id: "inter", name: "Inter", kind: "sans", family: "Inter Variable", fallback: SANS_FB, weights: [100, 900] },
  { id: "dm-sans", name: "DM Sans", kind: "sans", family: "DM Sans Variable", fallback: SANS_FB, weights: [100, 1000] },
  { id: "manrope", name: "Manrope", kind: "sans", family: "Manrope Variable", fallback: SANS_FB, weights: [200, 800] },
  { id: "plus-jakarta-sans", name: "Plus Jakarta Sans", kind: "sans", family: "Plus Jakarta Sans Variable", fallback: SANS_FB, weights: [200, 800] },
  { id: "ibm-plex-sans", name: "IBM Plex Sans", kind: "sans", family: "IBM Plex Sans Variable", fallback: SANS_FB, weights: [100, 700] },
  { id: "montserrat", name: "Montserrat", kind: "sans", family: "Montserrat Variable", fallback: SANS_FB, weights: [100, 900] },
  { id: "poppins", name: "Poppins", kind: "sans", family: "Poppins", fallback: SANS_FB, weights: [400, 700] },
  { id: "outfit", name: "Outfit", kind: "sans", family: "Outfit Variable", fallback: SANS_FB, weights: [100, 900] },
  { id: "space-grotesk", name: "Space Grotesk", kind: "sans", family: "Space Grotesk Variable", fallback: SANS_FB, weights: [300, 700] },
  { id: "instrument-serif", name: "Instrument Serif", kind: "serif", family: "Instrument Serif", fallback: SERIF_FB, weights: [400, 400] },
  { id: "playfair-display", name: "Playfair Display", kind: "serif", family: "Playfair Display Variable", fallback: SERIF_FB, weights: [400, 900] },
  { id: "fraunces", name: "Fraunces", kind: "serif", family: "Fraunces Variable", fallback: SERIF_FB, weights: [100, 900] },
  { id: "lora", name: "Lora", kind: "serif", family: "Lora Variable", fallback: SERIF_FB, weights: [400, 700] },
  { id: "source-serif-4", name: "Source Serif 4", kind: "serif", family: "Source Serif 4 Variable", fallback: SERIF_FB, weights: [200, 900] },
  { id: "dm-serif-display", name: "DM Serif Display", kind: "serif", family: "DM Serif Display", fallback: SERIF_FB, weights: [400, 400] },
  { id: "bebas-neue", name: "Bebas Neue", kind: "display", family: "Bebas Neue", fallback: "Impact, 'Arial Narrow', sans-serif", weights: [400, 400] },
  { id: "syne", name: "Syne", kind: "display", family: "Syne Variable", fallback: SANS_FB, weights: [400, 800] },
  { id: "jetbrains-mono", name: "JetBrains Mono", kind: "mono", family: "JetBrains Mono Variable", fallback: "ui-monospace, Menlo, Consolas, monospace", weights: [100, 800] },
  { id: "caveat", name: "Caveat", kind: "hand", family: "Caveat", fallback: "'Comic Sans MS', cursive", weights: [500, 600] },
];

export const FONT: Record<string, FontSpec> = Object.fromEntries(FONTS.map((f) => [f.id, f]));

/** A box's font: the deck's title or body font (they follow the theme), or one of FONTS by id. */
export type SlideFont = "title" | "body" | (string & {});

/** A font as kept: "title", "body", or a font of the list by its id or its name; null for one the board does not host. */
export function cleanFont(raw: unknown): SlideFont | null {
  if (typeof raw !== "string") return null;
  const k = raw.trim().toLowerCase();
  if (k === "title" || k === "heading" || k === "headings") return "title";
  if (k === "body" || k === "text") return "body";
  const id = k.replace(/['"]/g, "").replace(/\s+variable$/, "").replace(/[\s_]+/g, "-");
  if (FONT[id]) return id;
  return FONTS.find((f) => f.name.toLowerCase() === k)?.id ?? null;
}

/** The CSS a font is drawn with: the theme's own for "title" and "body". */
export function fontStack(font: SlideFont | undefined, theme: { title: string; body: string }): string {
  if (!font || font === "body") return theme.body;
  if (font === "title") return theme.title;
  const f = FONT[font];
  return f ? `'${f.family}', ${f.fallback}` : theme.body;
}

// ---------------------------------------------------------------------------
// boxes

export type BoxKind = "text" | "shape" | "line" | "picture" | "widget" | "table" | "icon" | "chart" | "diagram";
export const BOX_KINDS: BoxKind[] = ["text", "shape", "line", "picture", "widget", "table", "icon", "chart", "diagram"];

/** The shapes, as PowerPoint and Google Slides offer the everyday ones. */
export const SHAPES = ["rect", "round", "ellipse", "triangle", "rtriangle", "diamond", "pentagon", "hexagon", "arrow", "chevron", "star", "bubble", "plus"] as const;
export type ShapeKind = (typeof SHAPES)[number];
export const SHAPE_NAMES: Record<ShapeKind, string> = {
  rect: "Rectangle",
  round: "Rounded rectangle",
  ellipse: "Ellipse",
  triangle: "Triangle",
  rtriangle: "Right triangle",
  diamond: "Diamond",
  pentagon: "Pentagon",
  hexagon: "Hexagon",
  arrow: "Arrow",
  chevron: "Chevron",
  star: "Star",
  bubble: "Speech bubble",
  plus: "Plus",
};

export type LineEnd = "none" | "arrow" | "dot";
/** An outline's dashes (SB: PowerPoint's dash dot, long dashes and square dots too, shared/slideFormat.ts). */
export type Dash = DashStyle;
export type Align = "left" | "center" | "right" | "justify";
export type VAlign = "top" | "middle" | "bottom";
/** How words fit their box: shrink to fit (the default, as the layouts do), the box grows with them, or neither (SA:
 * PowerPoint's "Do not Autofit", the words run past the box). */
export type TextFit = "shrink" | "grow" | "none";

/** How a box's words look, for the whole box: the words selected carry bold, italic, underline, strike, colour and links. */
export interface TextLook extends TextMore {
  font?: SlideFont;
  /** the type's size at 1600 by 900 */
  size?: number;
  /** the words' weight (bold words go heavier) */
  weight?: number;
  color?: SlideColor;
  align?: Align;
  valign?: VAlign;
  /** line height, times the size */
  lineH?: number;
  fit?: TextFit;
}

export interface SlideBox extends TextLook, LineMore, BoxFormat {
  id: string;
  kind: BoxKind;
  /** its place and size at 1600 by 900 (a line's from its start to its end, within this box) */
  x: number;
  y: number;
  w: number;
  h: number;
  /** degrees, clockwise */
  rot?: number;
  /** its place in the slide's layers: higher is in front */
  z: number;
  opacity?: number;
  shadow?: boolean;
  /** a shape's fill, or behind a text box's words; null: none */
  fill?: SlideColor | null;
  /** a border's colour, or a line's own; null: none */
  line?: SlideColor | null;
  /** a border's or a line's width */
  lineW?: number;
  dash?: Dash;
  /** a rectangle's or a picture's corners */
  radius?: number;
  shape?: ShapeKind;
  /** a line from the box's top right (flipH) or bottom left (flipV) corner, rather than its top left */
  flipH?: boolean;
  flipV?: boolean;
  /** a line's start and end */
  head?: LineEnd;
  tail?: LineEnd;
  /** a text box's or a shape's words: paragraphs and lists, with bold, italic, underline, strike, colour and links */
  text?: DocBody | null;
  image?: PictureRef;
  /** a picture fills its box (cropped) or fits inside it */
  imgFit?: "fill" | "fit";
  /** the point of the picture kept in sight when it fills, from 0 to 1 across and down */
  fx?: number;
  fy?: number;
  /** how far the picture is zoomed in its box (1: as it fills) */
  zoom?: number;
  /** a widget on this board */
  widget?: string;
  locked?: boolean;
  /** boxes grouped together share an id here: a click selects them all, a drag moves them together */
  group?: string;
  /** a table's grid and words (S7, shared/slideTable.ts); its header row stands on the box's fill, its borders are its line */
  table?: SlideTable;
  /** an icon (S7): its name in the board's set (Lucide's), drawn in the box's line colour, its strokes lineW on a grid of 24 */
  icon?: string;
  /** SD: a chart of the deck's own (shared/slideChart.ts), its words in the box's font and size */
  chart?: SlideChart;
  /** SD: a ready-made diagram from a list of points (shared/slideDiagram.ts) */
  diagram?: SlideDiagram;
}

/** The most boxes a slide holds, the words one box holds, and the pictures a slide holds. */
export const MAX_BOXES = 80;
export const MAX_BOX_TEXT = 4_000;
export const MAX_SLIDE_PICTURES = 24;

const BOX_ID = /^[A-Za-z0-9_-]{2,40}$/;
const ITEM_ID = /^[A-Za-z0-9_-]{4,40}$/;
const LINK_OK = /^(https?:|mailto:)/i;

const oneOf = <T extends string>(v: unknown, list: readonly T[]): T | undefined => (typeof v === "string" && (list as readonly string[]).includes(v) ? (v as T) : undefined);

/** Degrees as kept: from -180 to 180, 0 left out. */
export function cleanRot(v: unknown): number | undefined {
  if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
  let r = v % 360;
  if (r > 180) r -= 360;
  if (r <= -180) r += 360;
  r = Math.round(r * 100) / 100;
  return r === 0 || Object.is(r, -0) ? undefined : r;
}

/** A run of words with the marks a box keeps: bold, italic, underline, strike, a colour, a size, and links to the web. */
function boxInline(nodes: DocNode[] | undefined): DocNode[] {
  const out: DocNode[] = [];
  for (const n of nodes ?? []) {
    if (n.type === "hardBreak") out.push({ type: "hardBreak" });
    else if (n.type === "text" && typeof n.text === "string" && n.text) {
      const marks: DocMark[] = [];
      for (const m of n.marks ?? []) {
        if (m.type === "bold" || m.type === "italic" || m.type === "underline" || m.type === "strike") marks.push({ type: m.type });
        else if (m.type === "link" && typeof m.attrs?.href === "string" && LINK_OK.test(m.attrs.href)) marks.push({ type: "link", attrs: { href: m.attrs.href.slice(0, 2000) } });
        else if (m.type === "textStyle") {
          const color = cleanColor(m.attrs?.color);
          const size = num(m.attrs?.size, 6, 600, 1);
          if (color || size) marks.push({ type: "textStyle", attrs: { ...(color ? { color } : {}), ...(size ? { size } : {}) } });
        }
      }
      out.push(marks.length ? { type: "text", text: n.text, marks } : { type: "text", text: n.text });
    } else if (n.content) out.push(...boxInline(n.content));
  }
  return out;
}

const idOf = (n: DocNode) => (typeof n.attrs?.id === "string" && n.attrs.id ? { id: n.attrs.id.slice(0, 40) } : undefined);

function boxItem(item: DocNode, depth: number): DocNode {
  const content: DocNode[] = [];
  for (const c of item.content ?? []) {
    if (c.type === "bulletList" || c.type === "orderedList") {
      if (depth < MAX_LEVELS) content.push(boxList(c, depth + 1));
      else for (const it of c.content ?? []) content.push({ type: "paragraph", content: boxInline(it.content) });
    } else if (c.type === "paragraph" || c.type === "heading") content.push({ type: "paragraph", ...(idOf(c) ? { attrs: idOf(c) } : {}), content: boxInline(c.content) });
    else if (c.content) content.push({ type: "paragraph", content: boxInline(c.content) });
  }
  if (!content.length || content[0].type !== "paragraph") content.unshift({ type: "paragraph", content: [] });
  return { type: "listItem", content: content.map((p) => (p.type === "paragraph" && !p.content?.length ? { type: "paragraph", ...(p.attrs ? { attrs: p.attrs } : {}) } : p)) };
}

function boxList(list: DocNode, depth: number): DocNode {
  const type = list.type === "orderedList" ? "orderedList" : "bulletList";
  const start = type === "orderedList" && Number(list.attrs?.start) > 1 ? { start: Math.min(9999, Math.round(Number(list.attrs?.start))) } : {};
  const attrs = { ...(idOf(list) ?? {}), ...start };
  return { type, ...(Object.keys(attrs).length ? { attrs } : {}), content: (list.content ?? []).filter((it) => it && typeof it === "object").map((it) => boxItem(it, depth)) };
}

/** A box's words as kept: paragraphs and lists (five deep, SA), with the marks boxInline keeps; null for none. */
/** The boxes that flip (Fixes 8): mirrored in their own frame, before they turn, their words never mirrored. */
export const FLIP_KINDS: BoxKind[] = ["shape", "picture", "icon"];

/**
 * What clear formatting takes off a box's or a field's look (Fixes 8, PowerPoint's Clear All Formatting): the words' own
 * font, size, weight, colour and letter spacing, back to the theme's. Their alignment, spacing and lists stay.
 */
export const CHAR_LOOK = ["font", "size", "weight", "color", "letter"] as const;

/**
 * Words with every mark taken off but their links: bold, italic, underline, strike, a colour or a size (Fixes 8). Runs
 * that come to look alike join into one, as the editor keeps them: told otherwise, a box's editor took its words for
 * changed and its next save tripled them.
 */
export function unmarkedWords(body: DocBody | null | undefined): DocBody | null {
  if (!body) return null;
  const strip = (nodes: DocNode[] | undefined): DocNode[] | undefined => {
    if (!nodes) return undefined;
    const out: DocNode[] = [];
    for (const n of nodes) {
      if (n.type !== "text") {
        out.push(n.content ? { ...n, content: strip(n.content) } : n);
        continue;
      }
      const marks = (n.marks ?? []).filter((m: DocMark) => m.type === "link");
      const { marks: _m, ...plain } = n;
      const next: DocNode = marks.length ? { ...plain, marks } : plain;
      const last = out[out.length - 1];
      if (last?.type === "text" && JSON.stringify(last.marks ?? null) === JSON.stringify(next.marks ?? null)) out[out.length - 1] = { ...last, text: `${last.text ?? ""}${next.text ?? ""}` };
      else out.push(next);
    }
    return out;
  };
  return { ...body, content: strip(body.content) ?? [] };
}

export function cleanBoxText(raw: unknown): DocBody | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw) || (raw as DocBody).type !== "doc" || !Array.isArray((raw as DocBody).content)) return null;
  const out: DocNode[] = [];
  for (const b of (raw as DocBody).content) {
    if (!b || typeof b !== "object") continue;
    if (b.type === "bulletList" || b.type === "orderedList" || b.type === "taskList") out.push(boxList(b, 1));
    else if (b.type === "paragraph" || b.type === "heading" || b.type === "blockquote") {
      if (b.type === "blockquote") for (const p of b.content ?? []) out.push({ type: "paragraph", content: boxInline(p.content) });
      else {
        const content = boxInline(b.content);
        out.push({ type: "paragraph", ...(idOf(b) ? { attrs: idOf(b) } : {}), ...(content.length ? { content } : {}) });
      }
    }
  }
  if (!out.length) return null;
  return { type: "doc", content: out };
}

/** A box's words on one line, for lists, replies and limits. */
export function boxWords(text: DocBody | null | undefined): string {
  const walk = (n: DocNode): string => (n.type === "text" ? (n.text ?? "") : n.type === "hardBreak" ? " " : (n.content ?? []).map(walk).join(" "));
  return (text?.content ?? []).map(walk).join(" ").replace(/\s+/g, " ").trim();
}

/** The look a box's words have, as kept. */
function cleanTextLook(r: Record<string, unknown>): TextLook {
  const font = cleanFont(r.font);
  const size = num(r.size, 6, 600, 1);
  const weight = num(r.weight, 100, 900, 0);
  const color = cleanColor(r.color);
  const align = oneOf(r.align, ["left", "center", "right", "justify"] as const);
  const valign = oneOf(r.valign, ["top", "middle", "bottom"] as const);
  const lineH = num(r.lineH, 0.7, 3, 2);
  const fit = oneOf(r.fit, ["shrink", "grow", "none"] as const);
  return { ...(font ? { font } : {}), ...(size ? { size } : {}), ...(weight ? { weight } : {}), ...(color ? { color } : {}), ...(align ? { align } : {}), ...(valign ? { valign } : {}), ...(lineH ? { lineH } : {}), ...(fit ? { fit } : {}), ...cleanTextMore(r) };
}

/** A fill or a border's colour: a colour, null for none (said), or nothing (the kind's own). */
const colorOrNone = (r: Record<string, unknown>, k: string): { [k: string]: SlideColor | null } => {
  if (!Object.hasOwn(r, k)) return {};
  if (r[k] === null || r[k] === "none" || r[k] === "") return { [k]: null };
  const c = cleanColor(r[k]);
  return c ? { [k]: c } : {};
};

/**
 * A box as kept, or null when it cannot be one (no id, no kind, a picture with no picture the board keeps, a widget
 * with none). Numbers are kept within reason, unknown fields left out; nothing here refuses (limits are checkBoxes').
 */
export function cleanBox(raw: unknown, z = 0): SlideBox | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !BOX_ID.test(r.id)) return null;
  const kind = oneOf(r.kind, BOX_KINDS);
  if (!kind) return null;
  const line = kind === "line";
  const box: SlideBox = {
    id: r.id,
    kind,
    x: num(r.x, -2 * W, 3 * W) ?? 0,
    y: num(r.y, -2 * H, 3 * H) ?? 0,
    w: num(r.w, line ? 0 : 1, 4 * W) ?? (line ? 0 : 1),
    h: num(r.h, line ? 0 : 1, 4 * H) ?? (line ? 0 : 1),
    z: num(r.z, -1e6, 1e6, 3) ?? z,
  };
  const rot = line ? undefined : cleanRot(r.rot);
  if (rot !== undefined) box.rot = rot;
  const opacity = num(r.opacity, 0, 1);
  if (opacity !== undefined && opacity < 1) box.opacity = opacity;
  if (r.shadow === true) box.shadow = true;
  if (r.locked === true) box.locked = true;
  if (typeof r.group === "string" && BOX_ID.test(r.group)) box.group = r.group;
  Object.assign(box, colorOrNone(r, "fill"), colorOrNone(r, "line"));
  const lineW = num(r.lineW, 0, 64, 1);
  if (lineW !== undefined) box.lineW = lineW;
  const dash = oneOf(r.dash, DASHES);
  if (dash && dash !== "solid") box.dash = dash;
  if (kind === "shape" || kind === "picture" || kind === "text") {
    const radius = num(r.radius, 0, 450, 1);
    if (radius) box.radius = radius;
  }
  if (kind === "shape") box.shape = oneOf(r.shape, SHAPES) ?? "rect";
  // a line's flips say which way it runs; a shape, a picture or an icon is mirrored (Fixes 8)
  if (line || FLIP_KINDS.includes(kind)) {
    if (r.flipH === true) box.flipH = true;
    if (r.flipV === true) box.flipV = true;
  }
  if (line) {
    const head = oneOf(r.head, ["none", "arrow", "dot"] as const);
    const tail = oneOf(r.tail, ["none", "arrow", "dot"] as const);
    if (head && head !== "none") box.head = head;
    if (tail && tail !== "none") box.tail = tail;
    if (box.w < 1 && box.h < 1) box.w = 1;
    // SA: an elbow, and ends attached to boxes (shared/slidePlace.ts)
    Object.assign(box, cleanLineMore(r));
  }
  if (kind === "text" || kind === "shape") {
    Object.assign(box, cleanTextLook(r));
    const text = cleanBoxText(r.text);
    if (text) box.text = text;
  }
  if (kind === "picture") {
    const image = cleanPictureRef(r.image);
    if (!image) return null;
    box.image = image;
    if (r.imgFit === "fit") box.imgFit = "fit";
    const fx = num(r.fx, 0, 1, 3);
    const fy = num(r.fy, 0, 1, 3);
    const zoom = num(r.zoom, 1, 5, 3);
    if (fx !== undefined && fx !== 0.5) box.fx = fx;
    if (fy !== undefined && fy !== 0.5) box.fy = fy;
    if (zoom !== undefined && zoom > 1) box.zoom = zoom;
  }
  if (kind === "widget") {
    if (typeof r.widget !== "string" || !ITEM_ID.test(r.widget)) return null;
    box.widget = r.widget;
  }
  if (kind === "icon") {
    // a name of letters, digits and dashes; the browser draws it from the board's set, the agents' writes check it
    if (typeof r.icon !== "string" || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(r.icon) || r.icon.length > 48) return null;
    box.icon = r.icon;
    if (box.lineW !== undefined) box.lineW = Math.max(0.5, Math.min(4, box.lineW));
  }
  if (kind === "table") {
    // S7: a grid of cells; its words' look is the box's, its header's fill the box's fill, its borders the box's line
    const table = cleanTable(r.table);
    if (!table) return null;
    Object.assign(box, cleanTextLook(r));
    box.table = table;
  }
  if (kind === "chart" || kind === "diagram") {
    // SD: a chart's data and format, or a diagram's points and layout; their words take the box's font and size
    const inner = kind === "chart" ? cleanChart(r.chart) : cleanDiagram(r.diagram);
    if (!inner) return null;
    if (kind === "chart") box.chart = inner as SlideChart;
    else box.diagram = inner as SlideDiagram;
    const font = cleanFont(r.font);
    const size = num(r.size, 6, 200, 1);
    if (font) box.font = font;
    if (size) box.size = size;
  }
  // SB: gradient and picture fills, outline ends, shadows, glows, soft edges, crops and adjustments (shared/slideFormat.ts)
  Object.assign(box, cleanBoxFormat(r, kind));
  return box;
}

/** Boxes in the order they are drawn: back to front, by their layer, then by id. */
export function byLayer(boxes: SlideBox[]): SlideBox[] {
  return [...boxes].sort((a, b) => a.z - b.z || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** A slide's boxes as kept, from a whole list: each cleaned, a second with the same id left out, in layer order. */
export function cleanBoxes(raw: unknown): SlideBox[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: SlideBox[] = [];
  raw.forEach((b, i) => {
    const box = cleanBox(b, i);
    if (!box || seen.has(box.id)) return;
    seen.add(box.id);
    out.push(box);
  });
  return byLayer(out);
}

/** Boxes with a patch laid over them, by id: a box replaces the one with its id (or is added), null removes it. */
export function applyBoxSet(boxes: SlideBox[], set: unknown): SlideBox[] {
  if (!set || typeof set !== "object" || Array.isArray(set)) return boxes;
  const map = new Map(boxes.map((b) => [b.id, b]));
  const top = boxes.reduce((m, b) => Math.max(m, b.z), 0);
  let n = 0;
  for (const [id, v] of Object.entries(set as Record<string, unknown>)) {
    if (!BOX_ID.test(id)) continue;
    if (v === null) {
      map.delete(id);
      continue;
    }
    const box = cleanBox({ ...(v as object), id }, top + ++n);
    if (box) map.set(id, box);
  }
  return byLayer([...map.values()]);
}

/** Why a slide's boxes cannot be kept, in words a person or an agent reads; null when they can. */
export function boxLimits(boxes: SlideBox[]): string | null {
  if (boxes.length > MAX_BOXES) return `A slide holds up to ${MAX_BOXES} boxes; this one would have ${boxes.length}. Remove some, or put them on another slide.`;
  // SB: a shape or text box filled with a picture holds one too
  const pictures = boxes.filter((b) => b.kind === "picture" || b.fillPic).length;
  if (pictures > MAX_SLIDE_PICTURES) return `A slide holds up to ${MAX_SLIDE_PICTURES} pictures; this one would have ${pictures}.`;
  for (const b of boxes) {
    if (b.table) {
      const why = tableLimits(b.table, b.id);
      if (why) return why;
    }
    if (b.chart) {
      const why = chartLimits(b.chart, b.id);
      if (why) return why;
    }
    const n = boxWords(b.text).length;
    if (n > MAX_BOX_TEXT) return `A box holds up to ${MAX_BOX_TEXT.toLocaleString("en-US")} characters; box ${b.id} has ${n.toLocaleString("en-US")}. Shorten it, or split it across slides.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// a layout's fields, placed and styled on one slide

/** A layout's fields that can be moved and restyled as boxes (shared/slides.ts SlideField). */
export const FIELDS = ["title", "body", "body2", "figure", "cite", "image", "widget"] as const;
export type FieldName = (typeof FIELDS)[number];

/**
 * Where a layout's field sits on one slide and how it looks, only where changed: with x, y, w and h it is drawn there
 * (else where its layout puts it), hidden when deleted (its words kept for the layout's reset), and styled like a box.
 */
export interface FieldPlace extends TextLook {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  rot?: number;
  z?: number;
  hidden?: boolean;
}

export type SlidePlace = Partial<Record<FieldName, FieldPlace>>;

/** One field's place as kept, or null when it changes nothing. */
export function cleanFieldPlace(raw: unknown): FieldPlace | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const out: FieldPlace = { ...cleanTextLook(r) };
  const x = num(r.x, -2 * W, 3 * W);
  const y = num(r.y, -2 * H, 3 * H);
  const w = num(r.w, 8, 4 * W);
  const h = num(r.h, 8, 4 * H);
  // a place is all four, or none
  if (x !== undefined && y !== undefined && w !== undefined && h !== undefined) Object.assign(out, { x, y, w, h });
  const rot = cleanRot(r.rot);
  if (rot !== undefined && out.x !== undefined) out.rot = rot;
  const z = num(r.z, -1e6, 1e6, 3);
  if (z !== undefined) out.z = z;
  if (r.hidden === true) out.hidden = true;
  return Object.keys(out).length ? out : null;
}

/** A slide's places as kept, from a whole map. */
export function cleanPlace(raw: unknown): SlidePlace {
  const out: SlidePlace = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const f of FIELDS) {
    const p = cleanFieldPlace((raw as Record<string, unknown>)[f]);
    if (p) out[f] = p;
  }
  return out;
}

/** Places with a patch laid over them, field by field: null puts a field back to its layout's own. */
export function applyPlaceSet(place: SlidePlace, set: unknown): SlidePlace {
  if (!set || typeof set !== "object" || Array.isArray(set)) return place;
  const out: SlidePlace = { ...place };
  for (const f of FIELDS) {
    if (!Object.hasOwn(set, f)) continue;
    const p = cleanFieldPlace((set as Record<string, unknown>)[f]);
    if (p) out[f] = p;
    else delete out[f];
  }
  return out;
}

/** Whether a field is drawn where its place says, rather than where its layout puts it. */
export const isPlaced = (p: FieldPlace | undefined): p is FieldPlace & { x: number; y: number; w: number; h: number } => !!p && p.x !== undefined && p.y !== undefined && p.w !== undefined && p.h !== undefined;

// ---------------------------------------------------------------------------
// a background (S6 builds what sets it: themes, a picture, saved layouts; the face draws it)

export type SlideBg =
  | { kind: "color"; color: SlideColor }
  | { kind: "gradient"; from: SlideColor; to: SlideColor; angle: number }
  /** a kept picture filling the slide (cropped) or fitting in it on a colour; veil darkens (below 0) or lightens it */
  | { kind: "picture"; image: PictureRef; fit: "fill" | "fit"; veil?: number; color?: SlideColor };

/** A background as kept, or null for the deck's own. */
export function cleanBg(raw: unknown): SlideBg | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (r.kind === "color") {
    const color = cleanColor(r.color);
    return color ? { kind: "color", color } : null;
  }
  if (r.kind === "gradient") {
    const from = cleanColor(r.from);
    const to = cleanColor(r.to);
    if (!from || !to) return null;
    const a = num(r.angle, -3600, 3600, 1) ?? 180;
    return { kind: "gradient", from, to, angle: ((a % 360) + 360) % 360 };
  }
  if (r.kind === "picture") {
    const image = cleanPictureRef(r.image);
    if (!image) return null;
    const veil = num(r.veil, -0.9, 0.9, 2);
    const color = cleanColor(r.color);
    return { kind: "picture", image, fit: r.fit === "fit" ? "fit" : "fill", ...(veil ? { veil } : {}), ...(color ? { color } : {}) };
  }
  return null;
}

// ---------------------------------------------------------------------------
// a change as it goes to the server

/** Whether a slide's put is a patch (the fields and boxes it changed), to lay over the slide rather than replace it. */
export function isSlidePatch(e: Pick<Entry, "t" | "data">): boolean {
  return e.t === "slide" && !!e.data && (Object.hasOwn(e.data, "boxSet") || Object.hasOwn(e.data, "placeSet"));
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * The entry a change to a slide is sent as: the fields it changed (null clears one), its boxes by id and its fields'
 * places by field, over the slide as the server has it. A slide the server does not have yet goes whole.
 */
export function wireSlide(before: Entry | null, after: Entry): Entry {
  if (after.t !== "slide" || !before || before.t !== "slide" || isSlidePatch(after)) return after;
  const a = before.data;
  const b = after.data;
  const data: Record<string, unknown> = {};
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (k === "boxes" || k === "place" || k === "boxSet" || k === "placeSet") continue;
    if (!same(a[k], b[k])) data[k] = b[k] ?? null;
  }
  const boxSet: Record<string, SlideBox | null> = {};
  const was = new Map(((Array.isArray(a.boxes) ? a.boxes : []) as SlideBox[]).map((x) => [x.id, x]));
  const now = new Map(((Array.isArray(b.boxes) ? b.boxes : []) as SlideBox[]).map((x) => [x.id, x]));
  for (const [id, x] of now) if (!same(was.get(id), x)) boxSet[id] = x;
  for (const id of was.keys()) if (!now.has(id)) boxSet[id] = null;
  data.boxSet = boxSet;
  const pa = (a.place ?? {}) as Record<string, unknown>;
  const pb = (b.place ?? {}) as Record<string, unknown>;
  const placeSet: Record<string, unknown> = {};
  for (const f of new Set([...Object.keys(pa), ...Object.keys(pb)])) if (!same(pa[f], pb[f])) placeSet[f] = pb[f] ?? null;
  if (Object.keys(placeSet).length) data.placeSet = placeSet;
  return { ...after, data };
}

/** Two patches to one slide as one, the later winning field by field and box by box. */
export function joinSlidePatches(a: Entry, b: Entry): Entry {
  const { boxSet: sa, placeSet: pa, ...ra } = a.data as Record<string, unknown>;
  const { boxSet: sb, placeSet: pb, ...rb } = b.data as Record<string, unknown>;
  const placeSet = { ...((pa as object) ?? {}), ...((pb as object) ?? {}) };
  return { ...b, data: { ...ra, ...rb, boxSet: { ...((sa as object) ?? {}), ...((sb as object) ?? {}) }, ...(Object.keys(placeSet).length ? { placeSet } : {}) } };
}

// ---------------------------------------------------------------------------
// places in percent, for agents (0 to 100 across and down the slide), and back

export const pctX = (px: number) => Math.round((px / W) * 1000) / 10;
export const pctY = (px: number) => Math.round((px / H) * 1000) / 10;
export const fromPctX = (p: number) => Math.round((p / 100) * W * 100) / 100;
export const fromPctY = (p: number) => Math.round((p / 100) * H * 100) / 100;

/** A new box's id. */
export const boxId = (rand: () => string) => "b" + rand().replace(/[^A-Za-z0-9]/g, "").slice(0, 10);
